import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

export const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, isAuthenticated, refreshUser } = useAuth();
  const location = useLocation();

  const requestedPath = `${location.pathname || ''}${location.search || ''}${location.hash || ''}` || '/';
  const loginRedirect = `/login?next=${encodeURIComponent(requestedPath)}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to={loginRedirect} replace state={{ from: location }} />;
  }

  return (
    <RevalidatedRoute
      allowedRoles={allowedRoles}
      refreshUser={refreshUser}
      loginRedirect={loginRedirect}
    >
      {children}
    </RevalidatedRoute>
  );
};

const RevalidatedRoute = ({ children, allowedRoles, refreshUser, loginRedirect }) => {
  const { user, isAuthenticated } = useAuth();
  const [revalidating, setRevalidating] = useState(true);

  useEffect(() => {
    let active = true;

    if (typeof refreshUser === 'function') {
      Promise.resolve(refreshUser(true)).finally(() => {
        if (active) {
          setRevalidating(false);
        }
      });
      return () => {
        active = false;
      };
    }

    setRevalidating(false);
    return () => {
      active = false;
    };
  }, [refreshUser]);

  if (revalidating) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-royal-600" />
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to={loginRedirect} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    switch (user?.role) {
      case 'admin':
        return <Navigate to="/admin" replace />;
      case 'landlord':
        return <Navigate to="/landlord" replace />;
      case 'tenant':
        return <Navigate to="/tenant" replace />;
      default:
        return <Navigate to="/" replace />;
    }
  }

  return children;
};
